import { Column, Index } from 'typeorm';
import { Entity } from '../../../decorators';
import { BaseEntity } from '../base.entity';

/**
 * Per-participant-per-conversation unread tracking. Split from
 * chat_participant so updating unread counts on every inbound message
 * doesn't touch the membership row (which is read-heavy by the member
 * list UI).
 *
 * One row per participant — created lazily on first message received.
 * The persistence subscriber upserts this row and increments unreadCount
 * as messages arrive; the UI hits a "mark read" outbox envelope which
 * resets it.
 *
 * Not auditable: pure counter state, rewritten on every message and
 * every read. Auditing it produced up to one `_audit` row per recipient
 * per message — the single largest write-amplification in the chat
 * system — for values with zero forensic value.
 */
@Entity('chat_read_state', {}, { auditable: false })
@Index('uq_chat_read_state_participant', ['conversationId', 'participantId'], {
	unique: true,
})
@Index('idx_chat_read_state_participant', ['participantId'])
export class ChatReadStateEntity extends BaseEntity {
	@Column('int', {
		name: 'fk_chat_conversation__id',
		unsigned: true,
	})
	public conversationId!: number;

	@Column('int', {
		name: 'fk_chat_participant__id',
		unsigned: true,
	})
	public participantId!: number;

	@Column('int', {
		name: 'fk_chat_message__id_last_read',
		nullable: true,
		unsigned: true,
	})
	public lastReadMessageId?: number;

	@Column('datetime', { name: 'lastReadAt', nullable: true, precision: 3 })
	public lastReadAt?: Date;

	@Column('int', { name: 'unreadCount', default: 0 })
	public unreadCount!: number;

	/** @mentions specifically — drives the red-dot notification on nav. */
	@Column('int', { name: 'unreadMentionCount', default: 0 })
	public unreadMentionCount!: number;
}
